import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client.js';
import {
  Badge, Breadcrumbs, Card, Empty, ErrorNotice, Loading, Notice, Pager, fmtDate, useToast, vehicleLabel,
} from '../components/ui.jsx';

const LIMIT = 25;

export default function RegistryPage() {
  const { universalKey } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [insurerId, setInsurerId] = useState('');
  const [offset, setOffset] = useState(0);
  const [exporting, setExporting] = useState(false);

  const runExport = async () => {
    setExporting(true);
    try {
      await api.exportRegistry({ search, insurerId });
    } catch (err) {
      toast.error(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const insurers = useQuery({ queryKey: ['insurers'], queryFn: () => api.listInsurers().then((r) => r.data) });

  const list = useQuery({
    queryKey: ['registry', search, insurerId, offset],
    queryFn: () => api.listRegistry({ search, insurerId, limit: LIMIT, offset }),
    enabled: !universalKey,
  });

  const entry = useQuery({
    queryKey: ['registryEntry', universalKey],
    queryFn: () => api.getRegistryEntry(universalKey).then((r) => r.data),
    enabled: Boolean(universalKey),
  });

  if (universalKey) {
    return (
      <div className="page">
        <Breadcrumbs items={[
          { label: 'Dashboard', to: '/dashboard' },
          { label: 'Key registry', to: '/registry' },
          { label: universalKey },
        ]} />
        <div className="page-head">
          <div>
            <h1 className="mono">{universalKey}</h1>
            <div className="page-sub">
              One universal key, and the code every insurer uses for the same vehicle.
            </div>
          </div>
          <div className="spacer" />
          <button className="btn" onClick={() => navigate('/registry')}>Back to registry</button>
        </div>

        {entry.isLoading && <Loading rows={6} />}
        {entry.error && <ErrorNotice error={entry.error} />}
        {entry.data && <RegistryDetail entry={entry.data} />}
      </div>
    );
  }

  const rows = list.data?.data || [];
  const total = list.data?.meta?.total || 0;

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Key registry' }]} />
      <div className="page-head">
        <div>
          <h1>Universal key registry</h1>
          <div className="page-sub">
            Every vehicle in the GIIB master carries one universal key. Insurer codes attach to that key as
            each insurer's master is imported and approved.
          </div>
        </div>
        <div className="spacer" />
        <button className="btn btn-primary" onClick={runExport} disabled={exporting}>
          {exporting ? 'Exporting...' : 'Export to Excel'}
        </button>
      </div>

      <Card bodyClass="tight">
        <div className="filters">
          <div className="f grow">
            <label htmlFor="r-search">Search</label>
            <input
              id="r-search" placeholder="Universal key, make, model, or any insurer's code"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setOffset(0); }}
            />
          </div>
          <div className="f">
            <label htmlFor="r-insurer">Linked to insurer</label>
            <select id="r-insurer" value={insurerId} onChange={(e) => { setInsurerId(e.target.value); setOffset(0); }}>
              <option value="">Any</option>
              {(insurers.data || []).map((i) => <option key={i.id} value={i.id}>{i.shortCode}</option>)}
            </select>
          </div>
        </div>

        {list.isLoading ? <div style={{ padding: 16 }}><Loading rows={6} /></div>
          : list.error ? <div style={{ padding: 16 }}><ErrorNotice error={list.error} /></div>
            : rows.length === 0 ? <Empty title="No vehicles found" />
              : (
                <>
                  <div className="table-wrap">
                    <table>
                      <thead>
                        <tr>
                          <th>Universal key</th>
                          <th>Vehicle</th>
                          <th className="num">cc / kW</th>
                          <th>Fuel</th>
                          <th className="num">Insurers linked</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((v) => (
                          <tr key={v.id} className="clickable" onClick={() => navigate(`/registry/${v.universalKey}`)}>
                            <td className="mono">{v.universalKey}</td>
                            <td>
                              <div className="cell-main">{vehicleLabel(v)}</div>
                            </td>
                            <td className="num">{v.cubicCapacity ?? (v.kilowatt ? `${v.kilowatt} kW` : '-')}</td>
                            <td>{v.fuelType || '-'}</td>
                            <td className="num">
                              {v.linkedInsurers > 0
                                ? <Badge kind="approved">{v.linkedInsurers}</Badge>
                                : <span className="prov">none yet</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <Pager total={total} limit={LIMIT} offset={offset} onChange={setOffset} />
                </>
              )}
      </Card>
    </div>
  );
}

function RegistryDetail({ entry }) {
  const v = entry.vehicle;
  return (
    <>
      <div className="cols cols-2">
        <Card title="Vehicle">
          <dl className="kv">
            <dt>Universal key</dt><dd className="mono">{entry.universalKey}</dd>
            <dt>Make</dt><dd>{v.make}</dd>
            <dt>Model</dt><dd>{v.model}</dd>
            <dt>Variant</dt><dd>{v.variant || '-'}</dd>
            <dt>Fuel type</dt><dd>{v.fuelType || '-'}</dd>
            <dt>Cubic capacity</dt><dd>{v.cubicCapacity ?? '-'}</dd>
            <dt>Power (kW)</dt><dd>{v.kilowatt ?? '-'}</dd>
            <dt>Seating</dt><dd>{v.seatingCapacity ?? '-'}</dd>
            <dt>Class</dt><dd>{v.classCode || '-'}</dd>
            <dt>Status</dt><dd><Badge kind={v.status === 'active' ? 'approved' : 'pending'}>{v.status}</Badge></dd>
            <dt>Added</dt><dd>{fmtDate(v.createdAt)}</dd>
          </dl>
          <Notice tone="info" title="Where these values come from">
            The universal key is minted by MMV Mapper. Every other field on this card is the GIIB master
            record for this vehicle.
          </Notice>
        </Card>

        <Card
          title="Insurer codes"
          subtitle={`${entry.coverage.approved} of ${entry.coverage.totalInsurers} active insurers have an approved link`}
        >
          {entry.links.length === 0 ? (
            <Empty title="No insurer codes linked yet">
              Import an insurer master and approve the match to create the first link.
            </Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Insurer</th><th>Code</th><th>Status</th><th>Band check</th><th>Approved by</th></tr>
                </thead>
                <tbody>
                  {entry.links.map((l) => (
                    <tr key={`${l.insurerId}-${l.code}`}>
                      <td>
                        <div className="cell-main">{l.insurerName}</div>
                        <div className="cell-sub">{l.insurerCode}</div>
                      </td>
                      <td>
                        <span className="mono">{l.code}</span>
                        <div className="prov">
                          {l.codeColumn ? `from ${l.codeColumn} in the insurer's file` : 'insurer code'}
                        </div>
                      </td>
                      <td><Badge kind={l.status}>{l.status}</Badge></td>
                      <td><Badge kind={l.ratingCheck}>{l.ratingCheck}</Badge></td>
                      <td>
                        {l.reviewedBy || <span className="prov">engine</span>}
                        <div className="prov">{fmtDate(l.updatedAt)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {entry.missingInsurers.length > 0 && (
            <Notice tone="warn" title={`${entry.missingInsurers.length} insurer(s) have no link to this vehicle`}>
              {entry.missingInsurers.map((i) => i.insurerCode).join(', ')} — either they do not carry this
              vehicle in their master, or their master has not been imported yet.
            </Notice>
          )}
        </Card>
      </div>

      <div style={{ marginTop: 16 }}>
        <Card title="Resolver API" subtitle="How a downstream system reads this mapping">
          <p style={{ marginTop: 0, color: 'var(--text-muted)' }}>
            Any insurer code resolves to the universal key, and the universal key returns every sibling code.
            Authenticate with an API key issued from the server, not a user session.
          </p>
          <pre className="mono" style={{
            background: 'var(--surface-2)', border: '1px solid var(--border)',
            borderRadius: 6, padding: 12, overflowX: 'auto',
          }}>
{`# resolve by universal key
curl -H "X-API-Key: $MMV_API_KEY" \\
  "$BASE/api/v1/resolve?universalKey=${entry.universalKey}"

# resolve by an insurer's own code${entry.links[0] ? `
curl -H "X-API-Key: $MMV_API_KEY" \\
  "$BASE/api/v1/resolve?insurerCode=${entry.links[0].insurerCode}&code=${entry.links[0].code}"` : ''}

# response
{
  "data": {
    "universalKey": "${entry.universalKey}",
    "vehicle": { "make": "${v.make}", "model": "${v.model}", "variant": ${JSON.stringify(v.variant)} },
    "codes": ${JSON.stringify(Object.fromEntries(entry.links.filter((l) => l.status === 'approved').map((l) => [l.insurerCode, l.code])), null, 6)}
  }
}`}
          </pre>
        </Card>
      </div>
    </>
  );
}
